"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Youtube, Tv, Headphones, Sparkles, Heart, BookOpen, ExternalLink } from "lucide-react";

const resources = [
  {
    title: "The Bible Project",
    description:
      "Engaging animated videos that explore the themes and design of the Bible. Perfect for helping kids (and adults!) understand the big story of Scripture through creative storytelling.",
    icon: BookOpen,
    link: "https://www.youtube.com/@bibleproject/playlists",
    platform: "YouTube",
    color: "from-blue-500/10 to-blue-600/10",
    iconColor: "text-blue-600",
  },
  {
    title: "Biblical TV Shows",
    description:
      "Faith-based television shows and series designed for children on RightNow Media. Access quality Christian entertainment that reinforces biblical values and teachings.",
    icon: Tv,
    link: "https://app.rightnowmedia.org/en/library/115",
    platform: "RightNow Media",
    color: "from-purple-500/10 to-purple-600/10",
    iconColor: "text-purple-600",
  },
  {
    title: "Minno Kids",
    description:
      "Fun, faith-filled shows and videos for kids that teach character, kindness, and biblical principles. Features original content and entertaining series your children will love.",
    icon: Sparkles,
    link: "https://www.youtube.com/@MinnoKids/videos",
    platform: "YouTube",
    color: "from-pink-500/10 to-pink-600/10",
    iconColor: "text-pink-600",
  },
  {
    title: "Listener Kids",
    description:
      "Audio adventures that bring Bible stories to life! These engaging podcasts and videos help children develop their faith through imaginative storytelling and biblical lessons.",
    icon: Headphones,
    link: "https://www.youtube.com/@listenerkids/videos",
    platform: "YouTube",
    color: "from-green-500/10 to-green-600/10",
    iconColor: "text-green-600",
  },
  {
    title: "Saddleback Kids",
    description:
      "High-energy videos and worship songs that make learning about Jesus exciting! Features fun lessons, music, and activities that engage kids in their faith journey.",
    icon: Heart,
    link: "https://www.youtube.com/playlist?list=PL5aPdmniG3y_n7hXEKTV4qQnIeCe-p6Ws",
    platform: "YouTube",
    color: "from-orange-500/10 to-orange-600/10",
    iconColor: "text-orange-600",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
  hover: {
    y: -8,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

export default function NextGenPage() {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!isClient) {
    return (
      <div className="bg-background">
        <section className="container flex flex-col gap-10 pb-16 pt-24">
          <div className="flex-1 space-y-6 text-center">
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Next Gen Resources
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
              Quality biblical content for kids and youth
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-background via-background to-secondary/20">
      {/* Hero Section */}
      <section className="container relative overflow-hidden pb-20 pt-24 md:pt-32">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="relative z-10 mx-auto max-w-4xl text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-6"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-primary backdrop-blur-sm">
              Next Generation Resources
            </span>
          </motion.div>

          <motion.h1
            variants={itemVariants}
            className="mb-6 text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl lg:text-6xl"
          >
            Faith Resources for Kids & Youth
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="mx-auto mb-10 max-w-2xl text-lg text-muted-foreground"
          >
            Quick links to quality biblical content for children and teenagers. From animated Bible
            stories to worship songs, find trusted resources to help your kids grow in their faith.
          </motion.p>
        </motion.div>

        {/* Background decoration */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
        </div>
      </section>

      {/* Resources Section */}
      <section className="border-t border-border/50 bg-background/80 py-20 backdrop-blur-sm">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="mx-auto max-w-6xl"
          >
            <div className="grid gap-6 md:grid-cols-2 lg:gap-8">
              {resources.map((resource) => {
                const Icon = resource.icon;
                return (
                  <motion.article
                    key={resource.title}
                    variants={cardVariants}
                    whileHover="hover"
                    className="group relative overflow-hidden rounded-2xl border border-border/50 bg-gradient-to-br from-background to-secondary/30 p-8 shadow-lg transition-shadow hover:shadow-xl"
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${resource.color} opacity-0 transition-opacity group-hover:opacity-100`}
                    />
                    <div className="relative space-y-4">
                      <div className="flex items-start justify-between">
                        <div
                          className={`inline-flex rounded-xl bg-gradient-to-br ${resource.color} p-3 transition-transform group-hover:scale-110`}
                        >
                          <Icon className={`h-6 w-6 ${resource.iconColor}`} />
                        </div>
                        <span className="flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-xs font-medium text-muted-foreground">
                          <Youtube className="h-3 w-3" />
                          {resource.platform}
                        </span>
                      </div>
                      <div>
                        <h3 className="mb-2 text-xl font-semibold text-foreground">
                          {resource.title}
                        </h3>
                        <p className="text-sm leading-relaxed text-muted-foreground">
                          {resource.description}
                        </p>
                      </div>
                      <div className="pt-2">
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          className="group/btn gap-2 transition-all hover:gap-3"
                        >
                          <a href={resource.link} target="_blank" rel="noopener noreferrer">
                            Visit Resource
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Info Section */}
      <section className="border-t border-border/50 bg-gradient-to-b from-secondary/20 to-background py-20">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-3xl rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-background p-12 text-center shadow-2xl backdrop-blur-sm"
          >
            <div className="mb-6 inline-flex rounded-full bg-primary/20 p-4">
              <Heart className="h-8 w-8 text-primary" />
            </div>
            <h2 className="mb-4 text-3xl font-bold text-foreground sm:text-4xl">
              Raising the Next Generation
            </h2>
            <p className="mb-6 text-lg text-muted-foreground">
              These resources are carefully selected to help parents equip their children with
              biblical knowledge and a strong foundation in faith. Each platform offers
              age-appropriate content that makes learning about God fun and engaging.
            </p>
            <p className="text-sm text-muted-foreground">
              Have a resource suggestion?{" "}
              <Link href="https://gkm.church/contact" className="font-medium text-primary hover:underline">
                Contact us
              </Link>{" "}
              to let us know!
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
